import type { Scene } from '@babylonjs/core/scene';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { CharacterAnimator } from '../rendering/CharacterAnimator';
import { DustEffect } from '../rendering/DustEffect';
import {
  baseClipFor,
  canCheer,
  crossedMilestone,
  BOTTLE_ANCHOR_NODE,
  drinkBottleScale,
  isCheerPickup,
  laneChangeClip,
  runSpeedRatio,
  stumbleClip,
} from '../characters/characterAnimation';
import { CharacterClip, type CharacterDefinition } from '../characters/CharacterTypes';
import type { CharacterConfig } from '../config/character.config';
import type { DozerPresentationConfig } from '../config/dozer.config';
import type { SeatPose } from '../dozer/DozerPresenter';
import type { EffectsConfig } from '../config/effects.config';
import type { RenderingConfig } from '../config/rendering.config';
import type { DeathReason } from '../core/GameEvents';
import type { TrackFrame } from '../level/TrackManager';
import type { TurnBend } from '../level/TurnController';
import { rightOf } from '../level/connectors/Pose';
import type { EntityVisual } from '../rendering/EntityVisual';
import type { VisualPool } from '../rendering/VisualPool';
import { clamp, clamp01, damp, DEG_TO_RAD, smoothstep, wrapAngle } from '../utils/math';
import type { PlayerSnapshot } from './PlayerController';
import { PlayerState } from './PlayerState';
import { bendPose, type BendPose } from './turnBend';

export interface PlayerRenderState {
  readonly snapshot: PlayerSnapshot;
  readonly frame: TrackFrame;
  readonly state: PlayerState;
  readonly invulnerable: boolean;
  readonly time: number;
  /** A run is in progress (otherwise the character idles on the start screen). */
  readonly running: boolean;
  readonly paused: boolean;
  /** Run speed (m/s) and difficulty speed 0..1 – pace of the run cycle. */
  readonly speed: number;
  readonly normalizedSpeed: number;
  /** The decided or last taken junction turn (TurnController.bend) – the body runs a bend around its corner. */
  readonly bend: TurnBend | null;
  /** Progress 0..1 of the extra-life drink (DrinkBreak), null while not drinking. */
  readonly drink: number | null;
  /** The current hit happened in the air (PlayerController.hitWhileAirborne). */
  readonly hitTrip: boolean;
  /** Riding the dozer: the seat of the machine this frame (DozerPresenter.seatPose), null otherwise. */
  readonly seat?: Readonly<SeatPose> | null;
}

/** A cut of the simulation (onto the seat, off it, thrown over the blade) the body blends over. */
interface SeatBlend {
  /** Where the body was drawn minus where it is now, at the cut (world space). */
  readonly offset: Vector3;
  readonly seconds: number;
  /** Height of the little arc on the way (m). */
  readonly arc: number;
  elapsed: number;
}

/**
 * Presentation of the player character: positions the visual from the
 * interpolated kinematic state and animates it. Rigged characters play their
 * clips (CharacterAnimator, state → clip in characterAnimation.ts); unrigged
 * placeholders fake lean/slide/hit poses. The character is a CharacterDefinition –
 * gameplay (PlayerController) is never coupled to a specific model.
 */
export class PlayerView {
  readonly root: TransformNode;
  private readonly pose: TransformNode;
  private visual!: EntityVisual;
  private animator: CharacterAnimator | null = null;
  private readonly dust: DustEffect;
  private lean = 0;
  private tilt = 0;
  private yaw = 0;
  private snapYaw = true;
  /** Body heading minus the heading it follows – eases out heading jumps no turn bend covers. */
  private yawLag = 0;
  private lastHeading = 0;
  private wasOnBend = false;
  private readonly bendScratch: BendPose = { offsetX: 0, offsetZ: 0, yaw: 0, speedFactor: 1 };
  private currentBend: BendPose | null = null;
  private deathReason: DeathReason | null = null;
  private lastStumble: CharacterClip | null = null;
  private lastCollectTime = Number.NEGATIVE_INFINITY;
  private lastCheerTime = Number.NEGATIVE_INFINITY;
  /** Time until which a requested cheer may still start (waits for the runner to land). */
  private cheerUntil = Number.NEGATIVE_INFINITY;
  private lastDistance = 0;
  /** The drink bottle (collectible model) on the rig's hand anchor – shown only while drinking. */
  private bottle: EntityVisual | null = null;
  /** Rendered on the dozer's seat last frame – a change is a cut the body blends over. */
  private seated = false;
  private seatBlend: SeatBlend | null = null;
  private readonly lastRendered = new Vector3();

  constructor(
    scene: Scene,
    private readonly pool: VisualPool,
    private character: CharacterDefinition,
    private readonly config: RenderingConfig['playerView'],
    private readonly characterConfig: CharacterConfig,
    dustConfig: EffectsConfig['dust'],
    /** The rider on the dozer: blends onto / off the seat, lean into steering, stand-in pose without ANIM_Drive. */
    private readonly rider: DozerPresentationConfig['rider'],
  ) {
    this.root = new TransformNode('PLAYER_ROOT', scene);
    this.pose = new TransformNode('PLAYER_POSE', scene);
    this.pose.parent = this.root;
    this.dust = new DustEffect(scene, this.root, dustConfig);
    this.acquireVisual();
  }

  /** Next render snaps to the track heading (new run); a model loaded since construction replaces its stand-in. */
  reset(): void {
    this.snapYaw = true;
    this.currentBend = null;
    if (this.visual.isFallback) this.replaceVisual();
    this.animator?.reset();
    this.dust.reset();
    this.deathReason = null;
    this.lastStumble = null;
    this.lastCollectTime = Number.NEGATIVE_INFINITY;
    this.lastCheerTime = Number.NEGATIVE_INFINITY;
    this.cheerUntil = Number.NEGATIVE_INFINITY;
    this.lastDistance = 0;
    this.seated = false;
    this.seatBlend = null;
  }

  /** The body's bend around a turn's corner this frame (null on the plain track path) – the camera follows it. */
  get bend(): Readonly<BendPose> | null {
    return this.currentBend;
  }

  /** True when the character is animated by clips (rigged model loaded). */
  get isAnimated(): boolean {
    return this.animator !== null;
  }

  get characterId(): string {
    return this.character.id;
  }

  /** Root of the runner's current model – a new one after a character switch or once the GLB replaced its stand-in. */
  get visualRoot(): TransformNode {
    return this.visual.root;
  }

  /**
   * Shows another character (selection on the start / game-over screen): the old model goes back
   * to the pool, the new one gets its own animator – its greybox stands in while the GLB is missing.
   */
  setCharacter(character: CharacterDefinition): void {
    if (character.id === this.character.id) return;
    this.character = character;
    this.replaceVisual();
    this.lastStumble = null;
    this.lastCollectTime = Number.NEGATIVE_INFINITY;
    this.lastCheerTime = Number.NEGATIVE_INFINITY;
    this.cheerUntil = Number.NEGATIVE_INFINITY;
  }

  // ── one-shot reactions (gameplay events) ─────────────────────────────────
  onLaneChanged(fromLane: number, toLane: number): void {
    this.animator?.trigger(laneChangeClip(fromLane, toLane));
  }

  onStumbled(lateralVelocity: number): void {
    const clip = stumbleClip(lateralVelocity, this.lastStumble);
    this.lastStumble = clip;
    this.animator?.trigger(clip);
  }

  onCollected(time: number, bottleValue: number): void {
    if (isCheerPickup(bottleValue, this.characterConfig)) {
      this.cheer(time);
      return;
    }
    if (time - this.lastCollectTime < this.characterConfig.collectCooldownSeconds) return;
    this.lastCollectTime = time;
    this.animator?.trigger(CharacterClip.Collect);
  }

  /** Extra life, bonus or distance milestone: fist pump as soon as the runner is on the ground. */
  cheer(time: number): void {
    if (time - this.lastCheerTime < this.characterConfig.cheer.cooldownSeconds) return;
    this.cheerUntil = time + this.characterConfig.cheer.pendingSeconds;
  }

  /** Dust puff on hard landings. */
  onLanded(impactSpeed: number): void {
    this.dust.land(impactSpeed);
  }

  onDied(reason: DeathReason): void {
    this.deathReason = reason;
  }

  render(dt: number, s: PlayerRenderState): void {
    const { snapshot, frame } = s;
    const seat = s.state === PlayerState.Driving ? (s.seat ?? null) : null;
    if (seat) {
      // On the dozer: the rig root on the seat socket (ANIM_Drive), or standing on the platform without the clip.
      this.currentBend = null;
      const onSeat = this.animator?.has(CharacterClip.Drive) ?? false;
      this.root.position.copyFrom(onSeat ? seat.position : seat.fallback);
      this.updateYaw(dt, seat.yaw, true);
    } else {
      const right = rightOf(frame.yaw);
      // The track path kinks 90° at a junction corner – the body runs a smooth bend around it instead.
      const bend = s.bend ? bendPose(s.bend, this.config.turnBendLength, this.bendScratch) : null;
      this.currentBend = bend;
      this.root.position.set(
        frame.x + right.x * snapshot.x + (bend?.offsetX ?? 0),
        frame.y + snapshot.y,
        frame.z + right.z * snapshot.x + (bend?.offsetZ ?? 0),
      );
      this.updateYaw(dt, bend?.yaw ?? frame.yaw, bend !== null);
    }
    this.blendSeat(dt, seat !== null, s.state);
    this.root.rotation.y = this.yaw;

    if (this.animator) this.animate(dt, s);
    else this.fakePose(dt, s);

    const blinkOff = s.invulnerable && Math.sin(s.time * this.config.invulnerableBlinkHz * Math.PI * 2) < 0;
    this.visual.root.setEnabled(!blinkOff);
    this.dust.update(s.running && !s.paused && s.state === PlayerState.Sliding);
  }

  /**
   * On a bend the body faces exactly along it (the bend heading is continuous). Any other
   * change of the followed heading becomes lag that eases out (turnYawDamping) – identical
   * to plain damping on curves, and it catches jumps no bend covers.
   */
  private updateYaw(dt: number, heading: number, onBend: boolean): void {
    if (this.snapYaw) this.yawLag = 0;
    else if (!onBend || !this.wasOnBend) this.yawLag = wrapAngle(this.yawLag + this.lastHeading - heading);
    this.yawLag = damp(this.yawLag, 0, this.config.turnYawDamping, dt);
    this.yaw = wrapAngle(heading + this.yawLag);
    this.lastHeading = heading;
    this.wasOnBend = onBend;
    this.snapYaw = false;
  }

  private animate(dt: number, s: PlayerRenderState): void {
    const cc = this.characterConfig;
    if (s.running && crossedMilestone(this.lastDistance, s.snapshot.distance, cc)) this.cheer(s.time);
    this.lastDistance = s.snapshot.distance;
    const drinking = s.drink !== null;
    const clip = baseClipFor(
      { state: s.state, running: s.running, normalizedSpeed: s.normalizedSpeed, deathReason: this.deathReason, drinking, hitTrip: s.hitTrip },
      cc,
    );
    if (this.bottle) {
      const scale = drinking ? drinkBottleScale(s.drink, cc) : 0;
      this.bottle.root.setEnabled(scale > 0);
      this.bottle.root.scaling.setAll(scale);
    }
    // On a turn's bend the body runs a little slower (cutting the corner) or faster (swinging out) than the track.
    this.animator?.setBase(clip, runSpeedRatio(clip, s.speed * (this.currentBend?.speedFactor ?? 1), cc));
    this.animator?.setPaused(s.paused);
    if (s.time <= this.cheerUntil && s.running && !s.paused && canCheer(s.state)) {
      this.cheerUntil = Number.NEGATIVE_INFINITY;
      this.lastCheerTime = s.time;
      this.animator?.trigger(CharacterClip.Celebrate);
    }
    this.animator?.update(dt);
    // The clips carry body motion; only a subtle lean into lane changes (or into the dozer's steering) is added here.
    const driving = s.state === PlayerState.Driving;
    const perSpeed = driving ? this.rider.leanDegreesPerLateralSpeed : cc.leanDegreesPerLateralSpeed;
    const maxLean = driving ? this.rider.maxLeanDegrees : cc.maxLeanDegrees;
    const targetLean = clamp(-s.snapshot.lateralVelocity * perSpeed, -maxLean, maxLean);
    this.lean = damp(this.lean, targetLean, this.config.poseDamping, dt);
    this.pose.position.y = 0;
    this.pose.rotation.x = 0;
    this.pose.rotation.z = this.lean * DEG_TO_RAD;
  }

  /**
   * The simulation cuts onto the seat (mount), off it (hop, catapult) and over the blade (thrown off by a hit): the
   * body keeps where it was drawn and blends to its new place on a little arc instead of jumping.
   */
  private blendSeat(dt: number, seated: boolean, state: PlayerState): void {
    if (seated !== this.seated) {
      const thrown = state === PlayerState.Hit || state === PlayerState.Dead;
      const r = this.rider;
      const seconds = seated ? r.mountSeconds : thrown ? r.throwSeconds : r.dismountSeconds;
      const arc = seated ? r.mountArc : thrown ? r.throwArc : 0;
      this.seatBlend = { offset: this.lastRendered.subtract(this.root.position), seconds, arc, elapsed: 0 };
      this.seated = seated;
    }
    const blend = this.seatBlend;
    if (blend) {
      blend.elapsed += dt;
      const u = clamp01(blend.elapsed / Math.max(blend.seconds, Number.EPSILON));
      const keep = 1 - smoothstep(u);
      this.root.position.addInPlaceFromFloats(blend.offset.x * keep, blend.offset.y * keep + blend.arc * 4 * u * (1 - u), blend.offset.z * keep);
      if (u >= 1) this.seatBlend = null;
    }
    this.lastRendered.copyFrom(this.root.position);
  }

  /** Unrigged placeholder: lean, bob and tilt instead of animation clips. */
  private fakePose(dt: number, s: PlayerRenderState): void {
    const c = this.config;
    const targetLean = clamp(-s.snapshot.lateralVelocity * c.leanDegreesPerLateralSpeed, -c.maxLeanDegrees, c.maxLeanDegrees);
    this.lean = damp(this.lean, targetLean, c.poseDamping, dt);

    let targetTilt = 0;
    if (s.state === PlayerState.Sliding) targetTilt = -c.slideTiltDegrees;
    else if (s.state === PlayerState.Hit) targetTilt = -c.hitTiltDegrees;
    else if (s.state === PlayerState.Dead) targetTilt = -c.deadTiltDegrees;
    this.tilt = damp(this.tilt, targetTilt, c.poseDamping * 0.6, dt);

    const running = s.state === PlayerState.Running || s.state === PlayerState.Stumbling;
    const bob = running ? Math.abs(Math.sin(s.snapshot.distance * c.runBobStepsPerMeter * Math.PI)) * c.runBobAmplitude : 0;
    this.pose.position.y = bob;
    this.pose.rotation.z = this.lean * DEG_TO_RAD;
    this.pose.rotation.x = this.tilt * DEG_TO_RAD;
  }

  private replaceVisual(): void {
    this.animator?.dispose();
    if (this.bottle) this.pool.release(this.bottle);
    this.bottle = null;
    this.pool.release(this.visual);
    this.acquireVisual();
  }

  private acquireVisual(): void {
    this.visual = this.pool.acquire(this.character.assetId, this.pose);
    this.animator = CharacterAnimator.create(this.visual.animationGroups ?? [], this.character, this.characterConfig);
    const anchor = this.visual.root.getChildTransformNodes(false).find((node) => node.name.endsWith(BOTTLE_ANCHOR_NODE));
    if (anchor) {
      this.bottle = this.pool.acquire(this.characterConfig.drinkBreak.bottle.assetId, anchor);
      this.bottle.root.setEnabled(false);
    }
    if (this.animator) {
      // Skinned meshes keep their bind-pose bounds – animated limbs (slide, flip) must never be culled.
      for (const mesh of this.visual.root.getChildMeshes()) mesh.alwaysSelectAsActiveMesh = true;
    }
  }
}
